import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { track } from '../../utils/tracking';

export default function WorkingCapitalPage() {
  useEffect(() => {
    track('page_view', { page: 'working_capital_detail' });
  }, []);

  useEffect(() => {
    document.title = 'Working Capital Financing | Short-Term Business Funding Solutions';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn about working capital financing for businesses. Understand requirements, benefits, application process, and how short-term funding can help manage cash flow and operations.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'working capital, business funding, short-term loans, cash flow financing, operational funding, business capital');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.href);
    }
  }, []);

  const faqs = [
    {
      question: 'What is working capital financing?',
      answer: 'Working capital financing is short-term funding designed to cover day-to-day operational expenses such as inventory purchases, payroll, rent, and other immediate business needs. Unlike long-term loans for major investments, working capital helps businesses maintain smooth operations and manage cash flow fluctuations.'
    },
    {
      question: 'How quickly can I get working capital funding?',
      answer: 'Working capital loans are among the fastest funding options available. Pre-qualification typically takes 5-10 minutes, approval decisions are often made within 24-72 hours, and funds can be deposited in your business bank account within 1-5 business days after approval.'
    },
    {
      question: 'What credit score do I need for working capital?',
      answer: 'Most lenders require a personal credit score of 600 or higher, with 650+ preferred for better terms and rates. Some alternative lenders may work with lower scores but typically offer higher rates and shorter repayment terms. Business credit is reviewed if available but is less critical than personal credit for working capital.'
    },
    {
      question: 'How much working capital can my business qualify for?',
      answer: 'Working capital amounts typically range from $10,000 to $500,000. The amount you qualify for depends on factors including monthly revenue, time in business, credit profile, and cash flow strength. Many lenders use a general guideline of 10-20% of annual revenue.'
    },
    {
      question: 'What documents are needed to apply?',
      answer: 'Basic requirements include 3-6 months of business bank statements, government-issued ID, business license or registration, and a voided business check. Some lenders may request tax returns or profit and loss statements for larger amounts, but documentation requirements are generally lighter than long-term loans.'
    },
    {
      question: 'How is working capital repaid?',
      answer: 'Repayment structures vary by lender. Common options include daily or weekly ACH debits from your business bank account, monthly payments similar to traditional loans, or percentage-of-revenue payments that adjust based on your sales. Terms typically range from 3-18 months.'
    },
    {
      question: 'Can startups get working capital financing?',
      answer: 'Most working capital lenders require at least 12 months in business, though some may work with businesses as young as 6 months. Startups under 6 months typically have difficulty qualifying for working capital and may need to explore alternative funding options like business credit cards or personal loans.'
    },
    {
      question: 'What are the typical interest rates for working capital?',
      answer: 'Rates vary widely based on creditworthiness, business strength, and lender type. Traditional bank working capital may range from 6-12% APR for well-qualified businesses. Alternative lenders typically charge 15-50% APR or use factor rates. Shorter terms and higher risk profiles generally result in higher costs.'
    }
  ];

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      
      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-gray-600 hover:text-amber-600 transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <span className="text-gray-900 font-semibold">Working Capital</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-white py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-4xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl shadow-lg">
                  <i className="ri-funds-line text-white text-3xl" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-amber-600 uppercase tracking-wide">
                    Funding Type
                  </div>
                  <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Working Capital Financing</h1>
                </div>
              </div>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Short-term financing designed to help businesses manage day-to-day operations, purchase inventory, cover payroll, and maintain healthy cash flow during seasonal fluctuations or growth periods.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/prequalification"
                  onClick={() => track('cta_clicked', { cta: 'hero_prequalification', funding_type: 'working_capital' })}
                  className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-lg font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Check Your Options
                  <i className="ri-arrow-right-line ml-2" />
                </Link>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg border-2 border-gray-300 hover:border-amber-500 transition-all whitespace-nowrap"
                >
                  Learn More
                  <i className="ri-arrow-down-line ml-2" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Stats */}
        <section className="py-12 px-4 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600 mb-2">$10K - $500K</div>
                <div className="text-sm text-gray-600">Funding Range</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600 mb-2">1-5 Days</div>
                <div className="text-sm text-gray-600">Funding Timeline</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600 mb-2">3-18 Months</div>
                <div className="text-sm text-gray-600">Typical Term</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600 mb-2">600+</div>
                <div className="text-sm text-gray-600">Min Credit Score</div>
              </div>
            </div>
          </div>
        </section>

        {/* What is Working Capital */}
        <section id="how-it-works" className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  What is Working Capital Financing?
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  Working capital financing provides short-term funding to cover operational expenses, inventory purchases, and cash flow gaps. Unlike long-term loans designed for major investments like equipment or real estate, working capital is specifically for immediate operational needs.
                </p>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  This type of financing helps businesses bridge the gap between accounts receivable and accounts payable, manage seasonal fluctuations, take advantage of supplier discounts, or simply maintain smooth operations during slower periods.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  Working capital loans are typically unsecured, meaning they don't require collateral, and have faster approval processes than traditional term loans. The trade-off is usually higher interest rates and shorter repayment terms.
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Key Features</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Fast Approval</div>
                      <div className="text-gray-600 text-sm">Decisions within 24-72 hours, funding in 1-5 days</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Flexible Use</div>
                      <div className="text-gray-600 text-sm">Use funds for any operational business need</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">No Collateral Required</div>
                      <div className="text-gray-600 text-sm">Most working capital loans are unsecured</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Short-Term Solution</div>
                      <div className="text-gray-600 text-sm">3-18 month terms for temporary needs</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Lighter Documentation</div>
                      <div className="text-gray-600 text-sm">Fewer documents than traditional loans</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Best Used For */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Best Used For
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Working capital financing is ideal for specific business situations where short-term funding can make a significant impact
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ri-line-chart-line',
                  title: 'Seasonal Cash Flow',
                  description: 'Bridge gaps during slow seasons or prepare for peak periods by purchasing inventory in advance'
                },
                {
                  icon: 'ri-shopping-cart-line',
                  title: 'Inventory Purchases',
                  description: 'Stock up on inventory for busy seasons or take advantage of bulk purchase discounts from suppliers'
                },
                {
                  icon: 'ri-team-line',
                  title: 'Payroll Coverage',
                  description: 'Ensure you can meet payroll obligations during slower periods or while waiting for customer payments'
                },
                {
                  icon: 'ri-price-tag-3-line',
                  title: 'Supplier Discounts',
                  description: 'Take advantage of early payment discounts or bulk purchase opportunities that require immediate cash'
                },
                {
                  icon: 'ri-calendar-check-line',
                  title: 'Receivables Gap',
                  description: 'Bridge the gap between when you pay suppliers and when customers pay you'
                },
                {
                  icon: 'ri-megaphone-line',
                  title: 'Marketing Campaigns',
                  description: 'Fund short-term marketing initiatives or seasonal advertising campaigns to drive revenue'
                }
              ].map((item, index) => (
                <div key={index} className="bg-white rounded-xl p-8 border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-lg mb-6">
                    <i className={`${item.icon} text-amber-600 text-2xl`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How Working Capital Financing Works
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding the process from application to funding
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Pre-Qualification',
                    timeline: '5-10 minutes',
                    description: 'Complete our online form with basic business information including revenue, time in business, and funding needs. Get initial feedback on potential options without impacting your credit score.'
                  },
                  {
                    step: '2',
                    title: 'Document Submission',
                    timeline: '1-2 hours',
                    description: 'Upload required documents including bank statements, ID, and business license. Most documents can be submitted digitally for faster processing. Documentation requirements are lighter than traditional loans.'
                  },
                  {
                    step: '3',
                    title: 'Review and Approval',
                    timeline: '24-72 hours',
                    description: 'Lenders review your application and documents, analyzing cash flow patterns and business health. You may receive multiple offers to compare terms, rates, and repayment structures.'
                  },
                  {
                    step: '4',
                    title: 'Funding',
                    timeline: '1-5 business days',
                    description: 'Once you accept an offer and sign agreements, funds are typically deposited directly into your business bank account within 1-5 business days. Some lenders offer same-day funding for urgent needs.'
                  }
                ].map((item, index) => (
                  <div key={index} className="bg-white rounded-xl p-8 border-2 border-gray-200 hover:border-amber-500 transition-colors">
                    <div className="flex items-start gap-6">
                      <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 text-white text-xl font-bold rounded-lg flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                          <span className="text-sm font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-full whitespace-nowrap">
                            {item.timeline}
                          </span>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Qualification Requirements */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Qualification Requirements
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                What lenders typically look for when evaluating working capital applications
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Business Requirements</h3>
                <div className="space-y-4">
                  {[
                    'Minimum 12 months in business',
                    'Monthly revenue of $30,000 or more',
                    'Active business bank account',
                    'Valid business license and tax ID',
                    'No recent bankruptcies or tax liens',
                    'Positive cash flow patterns'
                  ].map((req, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-amber-600" />
                      </div>
                      <span className="text-gray-700">{req}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl p-8 border border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Documentation Needed</h3>
                <div className="space-y-4">
                  {[
                    '3-6 months of business bank statements',
                    'Government-issued ID',
                    'Business license or registration',
                    'Voided business check',
                    'Recent profit and loss statement (may be required)',
                    'Business tax returns (for larger amounts)'
                  ].map((doc, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-file-list-3-line text-amber-600" />
                      </div>
                      <span className="text-gray-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pros and Cons */}
        <section className="py-20 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Advantages &amp; Considerations
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Understanding both the benefits and potential drawbacks helps you make informed decisions
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-emerald-500 rounded-xl">
                    <i className="ri-thumb-up-line text-white text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Advantages</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Fast approval and funding (1-5 days)',
                    'Lighter documentation requirements',
                    'No collateral required for most loans',
                    'Flexible use of funds for any operational need',
                    'Helps maintain cash flow during slow periods',
                    'Can improve credit by demonstrating repayment ability',
                    'Easier qualification than long-term loans',
                    'Multiple repayment structure options'
                  ].map((pro, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-emerald-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-500 rounded-xl">
                    <i className="ri-alert-line text-white text-xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">Considerations</h3>
                </div>
                <ul className="space-y-4">
                  {[
                    'Higher interest rates than traditional loans',
                    'Shorter repayment terms (3-18 months)',
                    'Daily or weekly payments can impact cash flow',
                    'Personal guarantee typically required',
                    'May include origination fees or other costs',
                    'Not suitable for long-term investments',
                    'Can create debt cycle if not managed properly',
                    'Credit score requirements still apply'
                  ].map((con, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-information-line text-amber-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">
                Common questions about working capital financing
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <details
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden group"
                >
                  <summary className="px-8 py-6 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                    <h3 className="text-lg font-bold text-gray-900 pr-8">{faq.question}</h3>
                    <i className="ri-add-line text-2xl text-amber-600 group-open:rotate-45 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-8 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Explore Working Capital Options?</h2>
            <p className="text-xl mb-8 text-white/90">
              Complete our pre-qualification form to see what working capital options may be available for your business.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/prequalification"
                onClick={() => track('cta_clicked', { cta: 'bottom_prequalification', funding_type: 'working_capital' })}
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap shadow-xl"
              >
                Start Pre-Qualification
                <i className="ri-arrow-right-line ml-2" />
              </Link>
              <Link
                to="/business-funding"
                className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white text-lg font-semibold rounded-lg border-2 border-white/30 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                View All Funding Types
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
